import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs/Subscription";
import { environment } from "../../../../environments/environment";
import { TranslateKey } from "../../../common/pipes/translate-key.pipe";
import { CacheService } from "../../../shared/services/cache.service";
import { CommonService } from "../../../shared/services/common.service";
import { AddressSchemaFormFrameworkType } from "../../enums/address-type.enum";
import { BusinessInfo } from "../../interface/business-info.interface";
import { JsonSchemaFormOptions } from "../../interface/enrollment/personal-info.interface";
import { AddressService } from "../../services/address.service";
import { ConfigurationService } from "../../services/configuration.service";
import { PersonalInfoConfigService } from "../../services/personal-info-config.service";
import { PersonalMessageService } from "../../services/personal-schema-form.service";
import { CustomSelectWidgetComponent } from "../../widgets/custom-select.widget";
import { CustomInputComponent } from "../../widgets/custom-textbox.widget";
import { PersonalInfoConfig } from "../../interface/personal-info-config.interface";
import { SchemaProperty } from '../../enums/schema-property.enum';

@Component({
    selector: 'app-business-info',
    templateUrl: '../../templates/template1/views/enrollment/business-info.component.html',
    styleUrls: ['../../templates/template1/themes/default/css/personal-info-form.component.less']
})

export class BusinessInfoComponent implements OnInit {

    @Input() preloadBusinessInformation: BusinessInfo;
    @Output() businessInfoSubmit = new EventEmitter<any>();
    @Output() businessInfoSubmitOnInputChange = new EventEmitter<any>();
    isoCountryCode: string;
    @Input() businessInfoSchemaUrl: string;
    jsonFormObject: any;
    personalInfoConfig: PersonalInfoConfig;
    originalJsonFormObject: any;
    selectedFrameWork = AddressSchemaFormFrameworkType.BOOTSTRAP4;
    jsonFormOptions: JsonSchemaFormOptions = {
        addSubmit: true, // Add a submit button if layout does not have one
        loadExternalAssets: false, // Load external css and JavaScript for frameworks
        debug: false,
        setSchemaDefaults: true,
        returnEmptyFields: false,
    };
    businessInfoFormValidated: boolean;
    businessInfoFormSubmitted: boolean;
    customWidgets = {
        'custom-select-widget': CustomSelectWidgetComponent,
        'custom-input-widget': CustomInputComponent,
    };
    interval: NodeJS.Timer;
    bonusInfoFormSubscription: Subscription;
    validationMessageInitialHide = true;
    displayBusinessInfoForm = false;
    businessInformation: BusinessInfo;
    liveFormData: BusinessInfo;
    businessInfoSubscription: Subscription;


    constructor(
        private _personalInfoConfigService: PersonalInfoConfigService,
        private _cacheService: CacheService,
        private _addressService: AddressService,
        private _elementRef: ElementRef,
        private _renderer: Renderer,
        private _translatePipe: TranslateService,
        private _translateKey: TranslateKey,
        private _commonService: CommonService,
        private _personalMessageService: PersonalMessageService,
        private _configurationService: ConfigurationService
    ) { }

    ngOnInit() {
        this.loadDefaultSettings();
    }

    
    private loadDefaultSettings(): void {
        this.isoCountryCode = this._configurationService.getEnrollCountry();
        this.personalInfoConfig = this._personalInfoConfigService.getSettings(this.isoCountryCode)
        this.getBusinessInfoJsonSchema();
        this.businessInfoFormSubject();
    }
    

    /**
     * @description this method will get Json Schema from service side
     * @author Akhil
     * @date 2021-08-25
     * @memberof BusinessInfoFormComponent
     */
    getBusinessInfoJsonSchema(): void {
        if (this.isoCountryCode || this.businessInfoSchemaUrl) {
            this.businessInfoSchemaUrl = this.businessInfoSchemaUrl || this.isoCountryCode.toLowerCase()
                + '-business-info.json?templateVersion=' + environment.templateVersion;
            this._addressService.getJsonFormSchema(this.businessInfoSchemaUrl)
                .subscribe((response: any) => {
                    this.jsonFormObject = this.getParsedSchemaFormJson(response);
                    this.originalJsonFormObject = response;
                    this.prePopulateUserInfoFields(this.jsonFormObject);
                    this.findErrorElement();
                });

        }
    }
    
    /**
     * @description this method will parse the JSON file 
     * @author Akhil
     * @date 2021-08-26
     * @param {*} jsonText
     * @returns {*}
     * @memberof BusinessInfoFormComponent
     */
    getParsedSchemaFormJson(jsonText: any): any {
        let newFormObject: any = null;
        try {
            // Parse entered content as JSON
            newFormObject = JSON.parse(jsonText);
            newFormObject = this.getTranslatedSchemaData(newFormObject);
        } catch (jsonError) {
            try {
                // If entered content is not valid JSON,
                // parse as JavaScript instead to include functions
                newFormObject = null;
                /* tslint:disable */
                // eval('newFormObject = ' + jsonText);
                /* tslint:enable */

            } catch (javascriptError) {
                return;
            }
        }
        return newFormObject;
    }
   
    /**
     * @description this method use to find Errors in Schema Form
     * @author Akhil
     * @date 2021-08-26
     * @memberof BusinessInfoFormComponent
     */
    findErrorElement(): void {
        this.interval = setInterval(() => {
            if (document.getElementsByClassName('help-block').length > 0) {
                this.hideErrorElement(true);
            }
        }, 100);
        setTimeout(() => {
            this.validationMessageInitialHide = false;
        }, 1000);
    }


    
    private hideErrorElement(hide: boolean): void {
        const elements = this._elementRef.nativeElement.getElementsByClassName('help-block');
        if (elements) {
            for (let i = 0; i < elements.length; i++) {
                if (hide) {
                    this._renderer.setElementClass(elements[i], 'hide', true);
                    this._renderer.setElementClass(elements[i], 'show', false);
                } else {
                    this._renderer.setElementClass(elements[i], 'show', true);
                    this._renderer.setElementClass(elements[i], 'hide', false);
                }
            }
            clearInterval(this.interval);
        }
    }

    /**
     * @description this method is used to prepopulate the data
     * @author Akhil
     * @date 2021-08-26
     * @param {*} jsonSchemaFormObject
     * @memberof BusinessInfoFormComponent
     */
    prePopulateUserInfoFields(jsonSchemaFormObject: any): void {
        this.jsonFormObject = jsonSchemaFormObject;
        if (this.preloadBusinessInformation) {
            this.jsonFormObject.data = this.preloadBusinessInformation;
            this.jsonFormObject = this.getTranslatedSchemaData(this.jsonFormObject);
        }
    }


    /**
     * @description this method used to translate the Schema Form Feilds
     * @author Akhil
     * @date 2021-08-26
     * @param {*} jsonObject
     * @returns {*}
     * @memberof BusinessInfoFormComponent
     */
    getTranslatedSchemaData(jsonObject: any): any {
        // Translate Schema form fields
        if (jsonObject && jsonObject.schema && jsonObject.schema.properties) {
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            if (jsonSchemaKeys) {
                jsonSchemaKeys.forEach((obj: any) => {
                    // Translate Schema Form fields..
                    let translatedText = jsonObject.schema.properties[obj].placeholder || jsonObject.schema.properties[obj].selectText;
                    if (translatedText.endsWith('*')) {
                        translatedText = translatedText.slice(0, -1);
                    }
                    let translatedValue = this._translatePipe.get('schemaform.' + translatedText)['value'];
                    translatedValue = translatedValue ? translatedValue : translatedText;
                    if (jsonObject.schema.required) {
                        if (jsonObject.schema.required.indexOf(obj) > -1) {
                            translatedValue += '*';
                        }
                    }
                    const translateKeyValue = this._translateKey.transform(translatedValue, false);
                    translatedValue = translateKeyValue ? translateKeyValue : translatedValue;
                    if (jsonObject.schema.properties[obj].placeholder) {
                        jsonObject.schema.properties[obj].placeholder = translatedValue;
                    } else if (jsonObject.schema.properties[obj].selectText) {
                        jsonObject.schema.properties[obj].selectText = translatedValue;
                    }
                    // Unique Validation Message Translation
                    if (jsonObject.schema.properties[obj].uniqueValidationMessage) {
                        const errorTranslatedText = jsonObject.schema.properties[obj].uniqueValidationMessage;
                        let errorTranslatedValue = this._translatePipe.get('schemaform.' + errorTranslatedText)['value'];
                        errorTranslatedValue = errorTranslatedValue ? errorTranslatedValue : errorTranslatedText;
                        const errorTranslateKeyValue = this._translateKey.transform(errorTranslatedValue, false);
                        errorTranslatedValue = errorTranslateKeyValue ? errorTranslateKeyValue : errorTranslatedValue;
                        jsonObject.schema.properties[obj].uniqueValidationMessage = errorTranslatedValue;
                    }
                    // Translate Validation messages..
                    if (jsonObject.schema.properties[obj].validationMessages) {
                        const validationMessagesKeys = Object.keys(jsonObject.schema.properties[obj].validationMessages);
                        validationMessagesKeys.forEach((validationObj) => {
                            if (validationObj) {
                                const errorTranslatedText = jsonObject.schema.properties[obj].validationMessages[validationObj];
                                if (errorTranslatedText) {
                                    let errorTranslatedValue = this._translatePipe.get('schemaform.' + errorTranslatedText)['value'];
                                    errorTranslatedValue = errorTranslatedValue ? errorTranslatedValue : errorTranslatedText;
                                    const errorTranslateKeyValue = this._translateKey.transform(errorTranslatedValue, false);
                                    errorTranslatedValue = errorTranslateKeyValue ? errorTranslateKeyValue : errorTranslatedValue;
                                    jsonObject.schema.properties[obj].validationMessages[validationObj] = errorTranslatedValue;
                                }
                            }
                        });
                    }
                });
            }
        }

        // Translate Schema form Button Text..
        if (jsonObject && jsonObject.form) {
            const jsonObjectForm = jsonObject.form;
            for (let i = 0; i < jsonObjectForm.length; i++) {
                jsonObjectForm[i].items = this.searchButtonTitle(jsonObjectForm[i].items);
            }
        }
        this.displayBusinessInfoForm = true;
        return jsonObject;
    }
   

    searchButtonTitle(items: any) {
        if (items) {
            items.forEach(element => {
                if (element.title) {
                    const translatedText = element.title;
                    let translatedValue = this._translatePipe.get('schemaform.' + translatedText)['value'];
                    translatedValue = this._translateKey.transform(translatedValue, false);
                    element.title = translatedValue;
                }
                if (element.items && element.items instanceof Array) {
                    const nestedItems = element.items;
                    this.searchButtonTitle(nestedItems);
                }
            });
        }
        return items;
    }

    /**
     * @description this method will check Businessinfo Valid or Not 
     * @author Akhil
     * @date 2021-08-30
     * @param {BusinessInfo} businessInfo
     * @memberof BusinessInfoComponent
     */
    saveBusinessInfo(businessInfo: BusinessInfo): void {
        const customErrors = this.getCustomErrors(businessInfo);
        if (businessInfo) {
            if ((!customErrors || (customErrors
                && !customErrors.find(x => x === 'InProgress')
                && (customErrors.length === 0 || customErrors[0] === '')))) {
                setTimeout(() => {
                    if (!customErrors || (customErrors
                        && !customErrors.find(x => x === 'InProgress')
                        && (customErrors.length === 0 || customErrors[0] === ''))) {
                        this.submitBusinessInfo(businessInfo, true);
                    } else if (this.preloadBusinessInformation
                        && this.preloadBusinessInformation.isAutoPopulate) {
                        this.submitBusinessInfo(businessInfo, true);
                    } else {
                        this.submitBusinessInfo(businessInfo, false);
                    }
                }, 2000);
            } else {
                this.submitBusinessInfo(businessInfo, false);
                this.hideErrorElement(false);
            }
        } else {
            this.submitBusinessInfo(businessInfo, false);
            this.hideErrorElement(false);
        }
    }
    
   
    getCustomErrors(businessInfo: BusinessInfo): any[] {
        const customErrors = [];
        const length: number = sessionStorage.length;
        for (let i = 0; i < length; i++) {
            const key = sessionStorage.key(i);
            if (!(sessionStorage[key] === 'true') && key.indexOf('customError-') > -1) {
                customErrors.push(sessionStorage[key]);
            }
        }
        return customErrors;
    }

    /**
     * @description this method will trigger onBusinessInfo Submit info
     * @author Akhil
     * @date 2021-08-30
     * @param {BusinessInfo} businessInfo
     * @param {boolean} businessInfoFormValidated
     * @memberof BusinessInfoComponent
     */
    submitBusinessInfo(businessInfo: BusinessInfo, businessInfoFormValidated: boolean) {
        this.businessInfoFormValidated = businessInfoFormValidated;
        if (businessInfo) {
            this.businessInformation = this._commonService.trimObjectValues(businessInfo);
            this.businessInformation.isBusinessInfoValid = businessInfoFormValidated;
        } else {
            this.hideErrorElement(false);
        }
        this.businessInfoSubmit.emit(this.businessInformation);
    }
   
    /**
     * @description this Method will on Input change of schema form Feilds 
     * @author Akhil
     * @date 2021-08-30
     * @param {*} event
     * @memberof BusinessInfoComponent
     */
    changeInputValue(event: any): void {
        this.businessInformation = event;
        this.businessInfoFormValidated = false;
        if (this.businessInformation) {
            this.businessInformation.isBusinessInfoValid = false;
        }
        this.businessInfoSubmitOnInputChange.emit(this.businessInformation);
    }
   
    onChanges(data: BusinessInfo) {
        this.liveFormData = data;
    
        if(this.personalInfoConfig.bonusInfoDynamicValidation){
            this.reloadJsonObject();
        }
    }
 
    private reloadJsonObject(): void {
        if (this.liveFormData &&
            (this.liveFormData.vatPayer
                || this.liveFormData.businessEntityId 
                || this.liveFormData.businessEntityName 
                || this.liveFormData.incomeTaxID 
                || this.liveFormData.city 
                || this.liveFormData.postalCode)) {
            const jsonObject = this.getParsedSchemaFormJson(this.originalJsonFormObject);
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            jsonSchemaKeys.forEach((obj: any) => {
                if (obj === SchemaProperty.ADDRESSLINE1) {
                    jsonObject.schema.required.push(obj);
                    jsonObject.schema.properties[obj].placeholder = jsonObject.schema.properties[obj].placeholder + '*';
                }
            });
            jsonObject.data = this.liveFormData;
            this.jsonFormObject = jsonObject;
        } else if (!this.liveFormData || (this.liveFormData &&
            !this.liveFormData.businessEntityName && !this.liveFormData.addressLine1
            && !this.liveFormData.city  && !this.liveFormData.businessEntityId && !this.liveFormData.vatPayer)) {
            const jsonObject = this.getParsedSchemaFormJson(this.originalJsonFormObject);
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            jsonSchemaKeys.forEach((obj: any) => {
                if (obj === SchemaProperty.VATPAYER || (obj === SchemaProperty.BUSINESSENTITYNAME &&
                    (jsonSchemaKeys.findIndex((ob) => ob === SchemaProperty.CITY) === -1)) ||
                    (obj === SchemaProperty.ADDRESSLINE1 && (jsonSchemaKeys.findIndex((ob) => ob === SchemaProperty.ADDRESSLINE1) === -1))) {
                    jsonObject.schema.required.splice(jsonObject.schema.required.indexOf(obj), 1);
                    if (jsonObject.schema.properties[obj].placeholder && jsonObject.schema.properties[obj].placeholder.endsWith('*')) {
                        jsonObject.schema.properties[obj].placeholder = jsonObject.schema.properties[obj].placeholder.slice(0, -1);
                    }
                }
            });
            jsonObject.data = this.liveFormData;
            this.jsonFormObject = jsonObject;
        }
    }
    /**
     * @description the subscription to listen BusinessInfo Submit event
     * @author Akhil
     * @date 2021-08-30
     * @private
     * @memberof BusinessInfoComponent
     */
    private businessInfoFormSubject(): void {
        this.businessInfoSubscription = this
            ._personalMessageService.getSubmitBusinessInfo()
            .subscribe(isSubmit => {
                this.submitBusinessInfoForm();
            });
    }
     
    /**
     * @description this method will externally  generate submit event fo Business Info 
     * @author Akhil
     * @date 2021-08-30
     * @memberof BusinessInfoComponent
     */
    submitBusinessInfoForm(): void {
        if (!this.businessInfoFormValidated) {
            const element = this._elementRef.nativeElement.getElementsByClassName('submit-info__btn');
            if (element && element[0]) {
                element[0].click();
            }
        }
    }

    ngOnDestroy() {
        if (this.businessInfoSubscription) {
            this.businessInfoSubscription.unsubscribe();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/enrollment/business-info.component.ts